import type { ImeiHubServiceId } from '@redevicer/shared'

export type VerifyLoadingStepId = string

export type VerifyLoadingStepDef = {
  id: VerifyLoadingStepId
  labelKey: string
  pendingKey: string
}

export type VerifyLoadingVariantConfig = {
  /** Optional headline override; falls back to vc.verifyLoading.title */
  titleKey?: string
  /** Detailed steps when backend exposes per-check progress (future). */
  detailedSteps: VerifyLoadingStepDef[]
}

/** Honest phases for a single combined API request (no fake per-database completion). */
export const VERIFY_LOADING_SINGLE_REQUEST_STEPS: VerifyLoadingStepDef[] = [
  {
    id: 'imei',
    labelKey: 'vc.verifyLoading.step.imei.label',
    pendingKey: 'vc.verifyLoading.step.imei.pending',
  },
  {
    id: 'connect',
    labelKey: 'vc.verifyLoading.step.connect.label',
    pendingKey: 'vc.verifyLoading.step.connect.pending',
  },
  {
    id: 'checking',
    labelKey: 'vc.verifyLoading.step.checking.label',
    pendingKey: 'vc.verifyLoading.step.checking.pending',
  },
  {
    id: 'analyze',
    labelKey: 'vc.verifyLoading.step.analyze.label',
    pendingKey: 'vc.verifyLoading.step.analyze.pending',
  },
  {
    id: 'report',
    labelKey: 'vc.verifyLoading.step.report.label',
    pendingKey: 'vc.verifyLoading.step.report.pending',
  },
]

const DETAILED_CARRIER: VerifyLoadingStepDef[] = [
  {
    id: 'imei',
    labelKey: 'vc.verifyLoading.detailed.imei.label',
    pendingKey: 'vc.verifyLoading.detailed.imei.pending',
  },
  {
    id: 'carrier',
    labelKey: 'vc.verifyLoading.detailed.carrier.label',
    pendingKey: 'vc.verifyLoading.detailed.carrier.pending',
  },
  {
    id: 'blacklist',
    labelKey: 'vc.verifyLoading.detailed.blacklist.label',
    pendingKey: 'vc.verifyLoading.detailed.blacklist.pending',
  },
  {
    id: 'finance',
    labelKey: 'vc.verifyLoading.detailed.finance.label',
    pendingKey: 'vc.verifyLoading.detailed.finance.pending',
  },
  {
    id: 'report',
    labelKey: 'vc.verifyLoading.step.report.label',
    pendingKey: 'vc.verifyLoading.step.report.pending',
  },
]

export const VERIFY_LOADING_CONFIG: Record<ImeiHubServiceId, VerifyLoadingVariantConfig> = {
  verify: {
    titleKey: 'vc.verifyLoading.title.verify',
    detailedSteps: VERIFY_LOADING_SINGLE_REQUEST_STEPS,
  },
  passport: {
    titleKey: 'vc.verifyLoading.title.passport',
    detailedSteps: VERIFY_LOADING_SINGLE_REQUEST_STEPS,
  },
  lock: {
    titleKey: 'vc.verifyLoading.title.lock',
    detailedSteps: VERIFY_LOADING_SINGLE_REQUEST_STEPS,
  },
  carrier: {
    titleKey: 'vc.verifyLoading.title.carrier',
    detailedSteps: DETAILED_CARRIER,
  },
  safecheck: {
    titleKey: 'vc.verifyLoading.title.safecheck',
    detailedSteps: VERIFY_LOADING_SINGLE_REQUEST_STEPS,
  },
  business: {
    titleKey: 'vc.verifyLoading.title.business',
    detailedSteps: VERIFY_LOADING_SINGLE_REQUEST_STEPS,
  },
  android: {
    titleKey: 'vc.verifyLoading.title.android',
    detailedSteps: VERIFY_LOADING_SINGLE_REQUEST_STEPS,
  },
  gsx: {
    titleKey: 'vc.verifyLoading.title.gsx',
    detailedSteps: VERIFY_LOADING_SINGLE_REQUEST_STEPS,
  },
  tmobile: {
    titleKey: 'vc.verifyLoading.title.tmobile',
    detailedSteps: VERIFY_LOADING_SINGLE_REQUEST_STEPS,
  },
}

export type VerifyLoadingStepsMode = 'single-request' | 'detailed'

export function getVerifyLoadingSteps(
  service: ImeiHubServiceId,
  mode: VerifyLoadingStepsMode = 'single-request',
): VerifyLoadingStepDef[] {
  if (mode === 'detailed') {
    return VERIFY_LOADING_CONFIG[service]?.detailedSteps ?? VERIFY_LOADING_SINGLE_REQUEST_STEPS
  }
  return VERIFY_LOADING_SINGLE_REQUEST_STEPS
}

export function getVerifyLoadingTitleKey(service: ImeiHubServiceId): string {
  return VERIFY_LOADING_CONFIG[service]?.titleKey ?? 'vc.verifyLoading.title'
}
